import type { ReactNode } from 'react';

import './Donut.styl';

export type DonutSlice = { key: string; label: string; value: number; tone: 'primary' | 'primary-2' | 'primary-4' | 'success' | 'warn' | 'ink' };

type Props = { slices: DonutSlice[]; size?: number; thickness?: number; center: ReactNode; label: string };

/**
 * Single-hue donut (primary ramp) or reserved tones for named categories; 2 px surface gaps between
 * segments; the legend beside it carries identity, never the color alone.
 */
export function Donut({ slices, size = 118, thickness = 22, center, label }: Props) {
  const total = slices.reduce((s, x) => s + x.value, 0);
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const gap = total > 0 ? Math.min(2, c / 50) : 0;
  const lengths = slices.map((s) => (total === 0 ? 0 : (s.value / total) * c));
  const offsets = lengths.map((_, i) => lengths.slice(0, i).reduce((a, b) => a + b, 0));
  return (
    <div className="c-donut">
      <svg className="c-donut__svg" width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label}>
        <circle className="c-donut__track" cx={size / 2} cy={size / 2} r={r} strokeWidth={thickness} fill="none" />
        {slices.map((s, i) => {
          const len = lengths[i] ?? 0;
          const offset = offsets[i] ?? 0;
          return (
            <circle
              key={s.key}
              className={`c-donut__seg c-donut__seg--${s.tone}`}
              cx={size / 2}
              cy={size / 2}
              r={r}
              strokeWidth={thickness}
              fill="none"
              strokeDasharray={`${Math.max(0, len - gap)} ${c - Math.max(0, len - gap)}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            >
              <title>{`${s.label}: ${s.value}`}</title>
            </circle>
          );
        })}
      </svg>
      <div className="c-donut__center">{center}</div>
    </div>
  );
}
